import { Component } from '@theme/component';
import { ThemeEvents, SellingPlanUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { CustomSelect } from '@theme/custom-select';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Subscription block: lets the customer pick one-time vs subscribe & save.
 * Renders one card per selling plan group, each with its own frequency
 * dropdown (a <custom-select>). Wires the selected selling plan into the
 * closest product form, swaps the add-to-cart CTA text, and re-renders
 * price details on variant or plan change.
 *
 * @typedef {object} SubscriptionRefs
 * @property {HTMLInputElement[]} [modeInputs]
 * @property {HTMLElement[]} [priceCurrent]
 * @property {HTMLElement[]} [priceCompare]
 * @property {HTMLElement[]} [subSubtitlePct]
 * @property {HTMLElement[]} [discountPctText]
 * @property {HTMLElement[]} [groupTitle]
 * @property {HTMLElement[]} [perDayPrice]
 *
 * @extends {Component<SubscriptionRefs>}
 */
class SubscriptionBlockComponent extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });

    const section = this.closest('.shopify-section, dialog');
    section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    // Re-assert the selected plan just before the form posts. A variant switch
    // re-renders the buybox and can drop the JS-added selling_plan input, which
    // would silently add the item as one-time (e.g. a preorder variant landing
    // in the cart with no preorder plan). Capture phase runs before the form's
    // own submit handler reads FormData.
    section?.addEventListener('submit', this.#onFormSubmit, { signal, capture: true });

    this.#normalizeSelection();
    this.#applyState();
  }

  /**
   * Collapse the mode radios down to a single checked one.
   *
   * Every card carries its own radio `name` (so each is a tab stop), so the
   * browser enforces no mutual exclusion — only #onChange does, and that needs a
   * change event. Any path that sets `checked` without one can leave two cards
   * selected: notably Firefox restoring control state on a soft reload, which
   * re-checks the previously selected card on top of the server-rendered
   * `checked` attribute. Re-assert the server's intent (data-initial-*) here so
   * the widget always starts from exactly one selection.
   */
  #normalizeSelection() {
    const inputs = /** @type {HTMLInputElement[]} */ ([
      ...this.querySelectorAll('input[data-mode]'),
    ]);
    if (inputs.length < 2) return;

    const checked = inputs.filter((input) => input.checked);
    if (checked.length === 1) return;

    const initialMode = this.dataset.initialMode;
    const initialGroupId = this.dataset.initialGroupId;
    const intended =
      inputs.find((input) =>
        initialMode === 'otp'
          ? input.dataset.mode === 'otp'
          : input.dataset.mode === 'subscribe' && input.dataset.groupId === initialGroupId
      ) ??
      checked[0] ??
      inputs[0];

    for (const input of inputs) input.checked = input === intended;
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#abortController?.abort();
  }

  /** @returns {'otp' | 'subscribe'} */
  #getMode() {
    const checked = this.querySelector('input[data-mode]:checked');
    if (checked instanceof HTMLInputElement && checked.dataset.mode === 'subscribe') {
      return 'subscribe';
    }
    return 'otp';
  }

  /** @returns {string} */
  #getActiveGroupId() {
    const checked = this.querySelector('input[data-mode="subscribe"]:checked');
    if (checked instanceof HTMLInputElement) {
      return checked.dataset.groupId ?? '';
    }
    return '';
  }

  /**
   * @param {string} groupId
   * @returns {CustomSelect | null}
   */
  #getDropdownForGroup(groupId) {
    const dropdown = this.querySelector(`custom-select[data-group-id="${groupId}"]`);
    return dropdown instanceof CustomSelect ? dropdown : null;
  }

  /** @returns {string} The currently selected plan id, or '' for OTP. */
  #getSelectedPlanId() {
    if (this.#getMode() !== 'subscribe') return '';
    const groupId = this.#getActiveGroupId();
    if (!groupId) return '';
    const dropdown = this.#getDropdownForGroup(groupId);
    return dropdown?.value ?? '';
  }

  /** @returns {HTMLFormElement | null} */
  #getProductForm() {
    const formId = this.dataset.formId;
    if (formId) {
      const byId = document.getElementById(formId);
      if (byId instanceof HTMLFormElement) return byId;
    }
    const section = this.closest('.shopify-section, dialog');
    const form = section?.querySelector('product-form-component form');
    return form instanceof HTMLFormElement ? form : null;
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;

    // Each card uses a unique radio name so every card is its own tab stop,
    // which means we have to enforce single-selection ourselves. Decide which
    // radio (if any) should become the active one for this change, then check
    // it and uncheck the rest.
    /** @type {HTMLInputElement | null} */
    let activeRadio = null;
    if (target instanceof CustomSelect && target.dataset.groupId) {
      // Picking a frequency in a group activates that group's radio.
      const radio = this.querySelector(
        `input[data-mode="subscribe"][data-group-id="${target.dataset.groupId}"]`
      );
      if (radio instanceof HTMLInputElement) activeRadio = radio;
    } else if (
      target instanceof HTMLInputElement &&
      target.type === 'radio' &&
      target.dataset.mode &&
      target.checked
    ) {
      activeRadio = target;
    }

    if (activeRadio) {
      activeRadio.checked = true;
      this.querySelectorAll('input[data-mode]').forEach((input) => {
        if (input instanceof HTMLInputElement && input !== activeRadio) input.checked = false;
      });
    }

    this.#applyState();
  };

  /**
   * Native radios respond to Space but not Enter. Allow Enter on a focused
   * mode radio to select that card.
   *
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key !== 'Enter') return;
    const target = event.target;
    if (
      target instanceof HTMLInputElement &&
      target.type === 'radio' &&
      target.dataset.mode
    ) {
      event.preventDefault();
      if (!target.checked) {
        target.checked = true;
        target.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  };

  /**
   * Guarantees the posted selling_plan matches the current selection, even if a
   * prior re-render dropped the hidden input.
   *
   * @param {Event} event
   */
  #onFormSubmit = (event) => {
    const form = this.#getProductForm();
    if (!form || event.target !== form) return;
    this.#updateSellingPlanInput(this.#getSelectedPlanId());
  };

  /** @param {Event} event */
  #onVariantUpdate = (event) => {
    const detail = /** @type {any} */ (event).detail;
    const html = detail?.data?.html;
    if (!(html instanceof Document)) return;

    this.refreshFrom(html);
  };

  /**
   * Morphs this block into its counterpart in a freshly rendered document, keeping
   * the shopper's purchase option (one-time vs subscribe, group and frequency).
   * Used for variant changes and for guest portal changes (see refreshForPortal at
   * the bottom of this file), where the new render carries a different discount
   * tier and therefore different plan ids.
   *
   * @param {Document} html
   */
  refreshFrom(html) {
    const blockId = this.dataset.blockId;
    if (!blockId) return;

    const next = html.querySelector(`subscription-block-component[data-block-id="${blockId}"]`);
    if (!(next instanceof HTMLElement)) return;

    const previousMode = this.#getMode();
    const previousGroupId = this.#getActiveGroupId();
    const previousPlanId = this.#getSelectedPlanId();
    const previousIntervalLabel =
      this.#getDropdownForGroup(previousGroupId)?.selectedOption?.dataset.intervalLabel;
    const previousGroupIndex = [...this.querySelectorAll('input[data-mode="subscribe"]')].findIndex(
      (input) => input instanceof HTMLInputElement && input.dataset.groupId === previousGroupId
    );

    morph(this, next);

    // Host attributes aren't morphed: track which portal this render belongs to.
    if (next.dataset.renderedPortal != null) {
      this.dataset.renderedPortal = next.dataset.renderedPortal;
    }

    // morph() runs children-only, so it never syncs this host element's own
    // attributes. Reconcile visibility ourselves: the re-rendered block carries
    // `hidden` exactly when the newly selected variant has no subscription plan.
    // Without this the inner content updates but the box stays hidden (or stays
    // visible) for the previous variant's plan state.
    this.toggleAttribute('hidden', next.hasAttribute('hidden'));

    // Carry the freshly-rendered variant's OTP price data onto this host element
    // so every subsequent #applyState() mirrors the price that belongs to the
    // newly selected variant rather than the one that was active on page load.
    if (next.dataset.otpPrice != null) {
      this.dataset.otpPrice = next.dataset.otpPrice;
    }
    if (next.dataset.otpPriceCents != null) {
      this.dataset.otpPriceCents = next.dataset.otpPriceCents;
    }

    // Same for the pre-order group: #updateCtaText() reads it to keep the Add label on a
    // pre-order card. It is empty for a variant that isn't a pre-order.
    if (next.dataset.preorderGroupId != null) {
      this.dataset.preorderGroupId = next.dataset.preorderGroupId;
    }

    // Each discount tier is its own selling plan group, so a tier change (guest portal
    // change) renders a different group id. Fall back to the group in the same position.
    const subscribeInputs = /** @type {HTMLInputElement[]} */ ([
      ...this.querySelectorAll('input[data-mode="subscribe"]'),
    ]);
    const targetGroupId = subscribeInputs.some((input) => input.dataset.groupId === previousGroupId)
      ? previousGroupId
      : (subscribeInputs[previousGroupIndex]?.dataset.groupId ?? previousGroupId);

    const inputs = this.querySelectorAll('input[data-mode]');
    let restored = false;
    inputs.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      if (previousMode === 'otp' && input.dataset.mode === 'otp') {
        input.checked = true;
        restored = true;
      } else if (
        previousMode === 'subscribe' &&
        input.dataset.mode === 'subscribe' &&
        input.dataset.groupId === targetGroupId
      ) {
        input.checked = true;
        restored = true;
      } else {
        input.checked = false;
      }
    });

    if (!restored) {
      const defaultChecked = this.querySelector('input[data-mode]:checked');
      if (!defaultChecked) {
        const firstInput = this.querySelector('input[data-mode]');
        if (firstInput instanceof HTMLInputElement) firstInput.checked = true;
      }
    }

    if (previousMode === 'subscribe' && previousPlanId) {
      const dropdown = this.#getDropdownForGroup(targetGroupId);
      if (dropdown) {
        dropdown.value = previousPlanId;
        // A tier change swaps every plan id, so the old one no longer exists. Keep
        // the same delivery frequency by matching its interval label instead.
        if (dropdown.value !== previousPlanId && previousIntervalLabel) {
          const sameInterval = dropdown.options.find(
            (option) => option.dataset.intervalLabel === previousIntervalLabel
          );
          if (sameInterval?.dataset.value) dropdown.value = sameInterval.dataset.value;
        }
      }
    }

    this.#clearTopPriceSnapshot();
    this.#applyState();
  }

  #applyState() {
    // Hidden = the current variant has no plan; the widget is rendered but not shown
    // (it exists only to listen for variant changes and be the morph target). Keep it
    // inert: just clear any stale subscription plan from the form so Add stays one-time,
    // and don't touch the CTA or price, which the buybox owns for a plan-less variant.
    // Once a variant switch morphs it visible (hidden removed), #applyState runs in full.
    if (this.hidden) {
      this.#updateSellingPlanInput('');
      this.#announceSelection('otp', '');
      return;
    }

    const mode = this.#getMode();
    const isSubscribe = mode === 'subscribe';

    const planId = this.#getSelectedPlanId();
    this.dataset.mode = mode;
    if (planId) {
      this.dataset.planId = planId;
    } else {
      delete this.dataset.planId;
    }

    this.#updateSellingPlanInput(planId);
    this.#updateCtaText(isSubscribe);
    this.#updateCtaPrice();
    this.#updateGroupDisplays();
    this.#updateTopPrice();
    this.#announceSelection(mode, planId);
  }

  /**
   * Tells the rest of the page which purchase option is now selected.
   *
   * Everything this block updates directly is inside the product form, so any
   * component outside it — the sticky add-to-cart bar in particular — would
   * otherwise keep reading "Add to cart" after a subscription was chosen. The
   * label is read back from the form rather than recomputed, so the two can
   * never disagree.
   *
   * @param {'subscribe' | 'otp'} mode
   * @param {string} planId
   */
  #announceSelection(mode, planId) {
    const form = this.#getProductForm();
    const label = form?.querySelector('.add-to-cart-text__label');

    this.dispatchEvent(
      new SellingPlanUpdateEvent({
        mode,
        planId,
        ctaLabel: label instanceof HTMLElement ? (label.textContent?.trim() ?? '') : '',
      })
    );
  }

  /** @param {string} planId */
  #updateSellingPlanInput(planId) {
    const form = this.#getProductForm();
    if (!form) return;

    const existing = form.querySelector('input[name="selling_plan"]');
    /** @type {HTMLInputElement} */
    let input;
    if (existing instanceof HTMLInputElement) {
      input = existing;
    } else {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'selling_plan';
      form.appendChild(input);
    }
    input.value = planId;
  }

  /** @param {boolean} isSubscribe */
  #updateCtaText(isSubscribe) {
    const form = this.#getProductForm();
    if (!form) return;

    // No subscribe text configured (e.g. mini-buybox) → leave the CTA label as rendered.
    const subscribeText = this.dataset.subscribeText;
    if (!subscribeText) return;

    // When the product is sold out the add-to-cart button is disabled and already
    // reads "Sold Out". Don't overwrite that with the subscribe text, or a
    // sold-out product would show an (inert) "Subscribe" CTA instead of "Sold Out".
    const addButton = form.querySelector('[name="add"]');
    if (addButton instanceof HTMLButtonElement && addButton.disabled) return;

    // A preorder box is technically a "subscribe" selling plan, but "Subscribe"
    // is the wrong CTA for it. Keep the original label (e.g. "+ Add") for the
    // preorder group; only real subscription boxes swap to the subscribe text.
    const preorderGroupId = this.dataset.preorderGroupId;
    const activeIsPreorder =
      preorderGroupId != null && preorderGroupId !== '' && this.#getActiveGroupId() === preorderGroupId;
    const useSubscribeText = isSubscribe && !activeIsPreorder;

    const textNodes = form.querySelectorAll('.add-to-cart-text__label');

    textNodes.forEach((node) => {
      if (!(node instanceof HTMLElement)) return;

      if (!node.dataset.originalCta) {
        node.dataset.originalCta = node.textContent?.trim() ?? '';
      }
      node.textContent = useSubscribeText ? subscribeText : node.dataset.originalCta;
    });
  }

  /**
   * Price for the currently-selected option: the active plan's price when
   * subscribing, otherwise the one-time-purchase price (data-otp-price).
   * @returns {string}
   */
  #getSelectedPriceText() {
    if (this.#getMode() === 'subscribe') {
      const dropdown = this.#getDropdownForGroup(this.#getActiveGroupId());
      const option = dropdown?.selectedOption;
      if (option instanceof HTMLElement && option.dataset.price) return option.dataset.price;
    }
    return this.dataset.otpPrice ?? '';
  }

  /** Mirror the selected price into any [data-cta-price] in the product form (mini-buybox CTA). */
  #updateCtaPrice() {
    const form = this.#getProductForm();
    if (!form) return;

    const price = this.#getSelectedPriceText();
    if (!price) return;

    form.querySelectorAll('[data-cta-price]').forEach((node) => {
      if (node instanceof HTMLElement) node.textContent = price;
    });
  }

  #updateGroupDisplays() {
    const dropdowns = this.querySelectorAll('custom-select[data-group-id]');
    dropdowns.forEach((dropdown) => {
      if (!(dropdown instanceof CustomSelect)) return;
      const groupId = dropdown.dataset.groupId;
      if (!groupId) return;
      const option = dropdown.selectedOption;
      if (!option) return;

      this.#setText(`[ref="subSubtitlePct[]"][data-group-id="${groupId}"]`, option.dataset.discountPct);
      this.#setText(`[ref="discountPctText[]"][data-group-id="${groupId}"]`, option.dataset.discountPct);
      this.#setText(`[ref="priceCurrent[]"][data-group-id="${groupId}"]`, option.dataset.price);
      this.#setText(`[ref="groupTitle[]"][data-group-id="${groupId}"]`, option.dataset.intervalLabel);
      this.#setText(`[ref="perDayPrice[]"][data-group-id="${groupId}"]`, option.dataset.perDayPrice);

      const compare = this.querySelector(`[ref="priceCompare[]"][data-group-id="${groupId}"]`);
      if (compare instanceof HTMLElement) {
        const hasDiscount = option.dataset.hasDiscount === 'true';
        if (hasDiscount && option.dataset.compare) {
          compare.textContent = option.dataset.compare;
          compare.classList.remove('tw:hidden');
        } else {
          compare.classList.add('tw:hidden');
        }
      }
    });
  }

  /** @returns {number} Active selection price in cents. */
  #getSelectedPriceCents() {
    if (this.#getMode() === 'subscribe') {
      const dropdown = this.#getDropdownForGroup(this.#getActiveGroupId());
      const option = dropdown?.selectedOption;
      if (option instanceof HTMLElement && option.dataset.priceCents) {
        return parseInt(option.dataset.priceCents, 10);
      }
    }
    return parseInt(this.dataset.otpPriceCents ?? '0', 10);
  }

  /**
   * Returns the <product-price> element that belongs to this block's own
   * .shopify-section or dialog ancestor. Scoping to the section prevents the
   * subscription widget from writing into product-price elements that live
   * elsewhere on the page (e.g. a featured-collection card showing the same
   * product ID).
   *
   * @returns {HTMLElement | null}
   */
  #getTopPriceEl() {
    const productId = this.dataset.productId;
    if (!productId) return null;
    const section = this.closest('.shopify-section, dialog');
    if (!section) return null;
    const priceEl = section.querySelector(`product-price[data-product-id="${productId}"]`);
    return priceEl instanceof HTMLElement ? priceEl : null;
  }

  /**
   * Mirrors the currently selected price (and compare-at) into the top-level
   * <product-price> element, and keeps the GST/VAT display in sync.
   *
   * On first page load, if a multi-variant product has not yet had a SKU
   * selected, the price element shows a min–max range via [data-price-selected]
   * hidden and [data-price-range] visible. Writing a single-variant price over
   * that range would be incorrect — the guard below exits early in that case so
   * the range remains visible until the shopper picks a variant.
   */
  #updateTopPrice() {
    const priceEl = this.#getTopPriceEl();
    if (!(priceEl instanceof HTMLElement)) return;

    // Guard: if [data-price-selected] exists and is still hidden, the
    // pre-selection price range is currently displayed. Bail out and let
    // product-price.js reveal the correct single-variant price when a SKU is
    // actually chosen, then allow subsequent #applyState() calls to run fully.
    const priceSelected = priceEl.querySelector('[data-price-selected]');
    if (priceSelected instanceof HTMLElement && priceSelected.hidden) return;

    const mode = this.#getMode();
    const isSubscribe = mode === 'subscribe';
    const priceText = this.#getSelectedPriceText();
    const priceCents = this.#getSelectedPriceCents();
    const priceNode = priceEl.querySelector('.price');
    if (priceNode instanceof HTMLElement && priceText) {
      priceNode.textContent = priceText;
    }
    const compareEl = priceEl.querySelector('.compare-at-price');
    if (compareEl instanceof HTMLElement) {
      if (!('subscriptionCompareSnapshot' in priceEl.dataset)) {
        priceEl.dataset.subscriptionCompareSnapshot = compareEl.textContent ?? '';
        priceEl.dataset.subscriptionCompareHidden = compareEl.classList.contains('tw:hidden') ? 'true' : 'false';
      }
      if (isSubscribe) {
        const dropdown = this.#getDropdownForGroup(this.#getActiveGroupId());
        const option = dropdown?.selectedOption;
        const hasDiscount = option instanceof HTMLElement && option.dataset.hasDiscount === 'true';
        const compareText = option instanceof HTMLElement ? option.dataset.compare : undefined;
        if (hasDiscount && compareText) {
          compareEl.textContent = compareText;
          compareEl.classList.remove('tw:hidden');
        } else {
          compareEl.classList.add('tw:hidden');
        }
      } else {
        compareEl.textContent = priceEl.dataset.subscriptionCompareSnapshot ?? '';
        if (priceEl.dataset.subscriptionCompareHidden === 'true') {
          compareEl.classList.add('tw:hidden');
        } else {
          compareEl.classList.remove('tw:hidden');
        }
      }
    }
    const vatEls = priceEl.querySelectorAll('.vat-display[data-context="product"]');
    vatEls.forEach((vatEl) => {
      if (vatEl instanceof HTMLElement) vatEl.dataset.price = String(priceCents);
    });
    window.renderVatDisplayBlocks?.();
  }

  /**
   * Removes the compare-at snapshot taken for the current variant so the next
   * variant's OTP state is captured fresh (called on variant change).
   */
  #clearTopPriceSnapshot() {
    const priceEl = this.#getTopPriceEl();
    if (!(priceEl instanceof HTMLElement)) return;
    delete priceEl.dataset.subscriptionCompareSnapshot;
    delete priceEl.dataset.subscriptionCompareHidden;
  }

  /**
   * @param {string} selector
   * @param {string | undefined} value
   */
  #setText(selector, value) {
    if (value == null) return;
    this.querySelectorAll(selector).forEach((node) => {
      if (node instanceof HTMLElement) node.textContent = value;
    });
  }
}

if (!customElements.get('subscription-block-component')) {
  customElements.define('subscription-block-component', SubscriptionBlockComponent);
}

/**
 * Standalone "Price per Day" line for PPC/UFO viewers, rendered by
 * subscription-widget.liquid in the slot the purchase-option cards occupied
 * before recurring subscriptions were withdrawn from those customers. The
 * figure is variant-dependent (price and servingSize both change), so on every
 * variant change it adopts its Liquid-rendered counterpart from the section
 * re-render — the same document subscription-block-component morphs from —
 * rather than recomputing anything client-side.
 */
class SubscriptionPerDay extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    this.#abortController = new AbortController();
    const section = this.closest('.shopify-section, dialog');
    section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /** @param {Event} event */
  #onVariantUpdate = (event) => {
    const html = /** @type {any} */ (event).detail?.data?.html;
    if (!(html instanceof Document)) return;

    const next = html.querySelector(`subscription-per-day[data-block-id="${this.dataset.blockId}"]`);
    if (!(next instanceof HTMLElement)) return;

    this.toggleAttribute('hidden', next.hasAttribute('hidden'));
    this.innerHTML = next.innerHTML;
  };
}

if (!customElements.get('subscription-per-day')) {
  customElements.define('subscription-per-day', SubscriptionPerDay);
}

/**
 * Re-renders one section and morphs each of its subscription blocks from the result.
 *
 * @param {string} sectionId
 * @param {SubscriptionBlockComponent[]} blocks
 * @returns {Promise<void>}
 */
async function refreshSectionBlocks(sectionId, blocks) {
  const url = new URL(window.location.href);
  // On the PDP, render the variant the shopper currently has selected rather than
  // the one in the URL (a variant switch may not have updated it).
  const formId = blocks[0]?.dataset.formId;
  const variantInput = formId ? document.getElementById(formId)?.querySelector('input[name="id"]') : null;
  if (url.pathname.includes('/products/') && variantInput instanceof HTMLInputElement && variantInput.value) {
    url.searchParams.set('variant', variantInput.value);
  }

  // cache: false — the section cache is keyed by URL, which hasn't changed, so a
  // cached entry would still be the render from before the portal changed.
  const sectionHTML = await sectionRenderer.getSectionHTML(sectionId, false, url);
  const html = new DOMParser().parseFromString(sectionHTML, 'text/html');
  blocks.forEach((block) => block.refreshFrom(html));
}

/**
 * MA-3013: a guest's Subscribe & Save tier comes from cart.attributes.portal_session,
 * which portal-manager.js may only set after this page was rendered (portal link
 * landing, or a fresh cart). Runs once portal-manager.js has finished its cart
 * update (`portal-manager:ready`): every guest block whose data-rendered-portal no
 * longer matches the portal is re-rendered via the Section Rendering API — once per
 * section, since the mini-buybox has several — so the discount, prices and submitted
 * selling plan match. Then clears `portal-pending` (snippets/portal-pending.liquid).
 *
 * Blocks inside a <dialog> are skipped: the subscription drawer and quick-add fetch
 * their bodies when opened, so they already render with the current attribute.
 */
async function refreshForPortal() {
  try {
    // Same resolution portal-manager.js uses for the cart: an empty ?portal_id= clears
    // it; otherwise the URL/cookie portal. With neither it leaves the cart untouched.
    // Read from portal-pending.liquid: portal-manager.js has already stripped an empty
    // ?portal_id= from the URL by now.
    const clearsPortal = /** @type {any} */ (window).portalIdParam === '';
    const portalManager = /** @type {any} */ (window).PortalManager;
    const portalId = clearsPortal ? '' : portalManager?.getPortalId?.();
    if (portalId == null) return;

    /** @type {Map<string, SubscriptionBlockComponent[]>} */
    const blocksBySection = new Map();
    document.querySelectorAll('subscription-block-component[data-rendered-portal]').forEach((block) => {
      if (!(block instanceof SubscriptionBlockComponent) || block.closest('dialog')) return;
      if (block.dataset.renderedPortal === portalId) return;
      const sectionId = block.closest('.shopify-section')?.id;
      if (!sectionId) return;
      const blocks = blocksBySection.get(sectionId) ?? [];
      blocks.push(block);
      blocksBySection.set(sectionId, blocks);
    });

    await Promise.allSettled(
      [...blocksBySection].map(([sectionId, blocks]) =>
        refreshSectionBlocks(sectionId, blocks).catch((error) => {
          console.error('Failed to refresh subscription pricing for portal change:', error);
        })
      )
    );
  } finally {
    document.documentElement.classList.remove('portal-pending');
  }
}

// portal-pending.liquid records the event in <head>, in case portal-manager.js
// finished before this module loaded.
if (/** @type {any} */ (window).portalManagerReady) {
  refreshForPortal();
} else {
  document.addEventListener('portal-manager:ready', refreshForPortal, { once: true });
}
